import { MutationTuple, QueryResult } from '@apollo/client/react/types/types';
import { useMutation, useQuery, MutationUpdaterFn } from '@apollo/client';
import { CREATE_INTEGRATION_TOKEN, REVOKE_INTEGRATION_TOKEN, GET_INTEGRATION_TOKENS } from './queries';
import { IApiIntegrationToken, IIntegrationToken } from './types';
import { useMemo } from 'react';
import { User } from 'src/models/users';

export function useLoadIntegrationTokens(): Omit<
  QueryResult<{ getIntegrationTokens: IApiIntegrationToken[] }>,
  'data'
> & { data?: IIntegrationToken[] } {
  const query = useQuery<{ getIntegrationTokens: IApiIntegrationToken[] }>(GET_INTEGRATION_TOKENS);
  return useMemo(
    () => ({
      ...query,
      data: query.data
        ? query.data.getIntegrationTokens.map(token => ({ ...token, owner: new User(token.owner) }))
        : undefined
    }),
    [query]
  );
}

export interface ICreateIntegrationToken {
  __typename: 'Mutation';
  createIntegrationToken: {
    __typename: 'PlainIntegrationToken';
    token: string;
  };
}

export function useCreateIntegrationToken(): MutationTuple<ICreateIntegrationToken, { name: string }> {
  return useMutation(CREATE_INTEGRATION_TOKEN);
}

export function useRevokeIntegrationToken(): MutationTuple<
  { revokeIntegrationToken: boolean; __typename: 'Mutation' },
  { hash: string }
> {
  return useMutation(REVOKE_INTEGRATION_TOKEN);
}
export function getUpdateCacheOnRevokeIntegrationToken(
  hash: string
): MutationUpdaterFn<{ revokeIntegrationToken: boolean; __typename: 'Mutation' }> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const tokensData = cache.readQuery<{ getIntegrationTokens: IApiIntegrationToken[] } | null>({
      query: GET_INTEGRATION_TOKENS
    });
    if (!tokensData) {
      return;
    }
    const { getIntegrationTokens } = tokensData;
    const newTokens = getIntegrationTokens.filter(token => token.hash !== hash);
    cache.writeQuery({
      query: GET_INTEGRATION_TOKENS,
      data: { getIntegrationTokens: newTokens }
    });
  };
}
